import { useContext, useEffect, useState } from 'react';

import { FormContext } from './FormContext';
import type { FormValidator } from './FormValidator';
import type { IFormConsumerState } from './IFormConsumerState';

export interface IFormFieldProps<T = unknown> {
    name: string;
    title: string;
    validators?: Array<FormValidator>;
    value: T;
    disabled?: boolean;
    onChange?: (value: T) => void;
    [name: string]: unknown;
    customRef?: React.Ref<unknown>;
    callbackOnBlur?: (value?: T) => void;
    type?: string;
}

export const useFormField = <T = unknown>(props: IFormFieldProps<T>) => {
    const context = useContext(FormContext) as IFormConsumerState<unknown>;
    const { name, value, onChange } = props;

    const [errors, setErrors] = useState<{ [key: string]: Array<string> }>({
        [name]: [],
    });
    const [formValue, setFormValue] = useState<T>(value);

    useEffect(() => {
        if (context?.setValue) {
            context.setValue(name, value as boolean | string | number | Date);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange =
        (fieldName: string, fieldValidators?: Array<FormValidator>) =>
        (event: unknown) => {
            let newValue: unknown;
            if (event && (event as { target?: unknown }).target !== undefined) {
                const target = (
                    event as {
                        target: {
                            checked?: boolean;
                            value?: unknown;
                            type?: string;
                        };
                    }
                ).target;

                newValue =
                    (target.type === 'checkbox' || target.type === 'radio') &&
                    target.checked !== undefined
                        ? target.checked
                        : target.value;
            } else {
                newValue = event;
            }

            const propErrors: Array<string> = [];

            if (fieldValidators) {
                fieldValidators.forEach((validator: FormValidator) => {
                    const validated = validator.params
                        ? validator.validator(newValue, validator.params)
                        : validator.validator(newValue);

                    if (!validated) {
                        propErrors.push(validator.error);
                    }
                });

                if (context) {
                    propErrors.length > 0
                        ? context.addError?.(fieldName)
                        : context.removeError?.(fieldName);
                }
            }

            setErrors({
                [fieldName]: propErrors,
            });
            setFormValue(newValue as T);

            if (context?.setValue) {
                context.setValue(
                    fieldName,
                    newValue as boolean | string | number | Date,
                );
            }

            if (onChange) {
                onChange(newValue as T);
            }
        };

    const hasError = () => {
        const error = errors[name];
        return error && error.length > 0;
    };

    const getHelperText = (): string | undefined => {
        const error = errors[name];
        return hasError() ? error.join(', ') : (props.helperText as string);
    };

    return {
        handleChange,
        hasError,
        getHelperText,
        formValue,
        errors,
        context,
    };
};
